import React, { useState, useEffect } from "react";
import {
  Dropdown,
  Option,
  Input,
  Label,
  makeStyles,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import SearchResourceInGraph from "../../GenericComponent/SearchResourceInGraph";
import QueueSelectorComponent from "../../GenericComponent/QueueSelectorComponent";
import GenerateAudioContainer from "../../../Containers/GenericContainer/GenerateAudioContainer";
import { VOICE_LIST } from "../../GenericComponent/GenericData/voiceList";
import "./CSS/PromptContainer.css";

// Choisit la langue par défaut la plus proche de celle du navigateur, parmi VOICE_LIST.
// Ex. navigateur "en-EU" → pas de match exact → "en-US" (préférence) ou 1er "en-*".
const pickClosestLanguage = () => {
  const codes = VOICE_LIST.map((v) => v.header);
  const candidates =
    (typeof navigator !== "undefined" &&
      (navigator.languages?.length ? navigator.languages : [navigator.language])) || [];
  // Régions préférées quand la langue seule est ambiguë (région ≠ code langue).
  const PREFERRED = { en: "en-US", zh: "zh-CN", ja: "ja-JP", ko: "ko-KR", ar: "ar-SA", pt: "pt-PT" };

  // 1) match exact (insensible à la casse)
  for (const c of candidates) {
    if (!c) continue;
    const exact = codes.find((h) => h.toLowerCase() === c.toLowerCase());
    if (exact) return exact;
  }
  // 2) même langue primaire : préférence connue → "xx-XX" → 1er "xx-*"
  for (const c of candidates) {
    const prim = (c || "").split("-")[0].toLowerCase();
    if (!prim) continue;
    const pref = PREFERRED[prim];
    if (pref && codes.includes(pref)) return pref;
    const sameRegion = codes.find((h) => h.toLowerCase() === `${prim}-${prim}`);
    if (sameRegion) return sameRegion;
    const byPrim = codes.find((h) => h.toLowerCase().startsWith(`${prim}-`));
    if (byPrim) return byPrim;
  }
  // 3) fallback global
  return codes.includes("en-US") ? "en-US" : codes[0];
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
});

export default function PromptContainer({ data, routingName, onChange, allRoutings = {}, ACTIVE_TENANT ,callFlowData, permissions, isEditMode}) {
  const classes = useStyles();
  const { t } = useTranslation();

  // Initialiser nextActionType correctement selon type et targetType
  const getInitialActionType = () => {
    if (data?.next?.type === "transfer") {
      return data?.next?.targetType === "phone" ? "transferPhone" : "transferUser";
    }
    return data?.next?.type || "";
  };

  const [nextActionType, setNextActionType] = useState(getInitialActionType());
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(data?.next?.targetId || "");
  const [selectedRouting, setSelectedRouting] = useState(data?.next?.routingName || "");

  // Nouveau prompt : pré-sélectionne la langue la plus proche de celle du navigateur.
  // On ne touche pas si une langue est déjà définie (prompt existant).
  useEffect(() => {
    if (!data?.audio?.language) {
      onChange(routingName, "audio.language", pickClosestLanguage());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fonction pour obtenir la valeur traduite du dropdown
  const getTranslatedValue = () => {
    if (data?.next?.type === "transfer") {
      return data?.next?.targetType === "phone" ? t("transferToPhone") : t("transferToUser");
    }
    const actionMap = {
      queue: "queue",
      disconnect: "disconnect",
      routing: "routing"
    };
    return t(actionMap[data?.next?.type] || data?.next?.type || "");
  };

  const handleNextActionSelect = (event, dataDropdown) => {
    const type = dataDropdown.optionValue;
    setNextActionType(type);
    setSelectedTarget(null);
    setPhoneNumber("");
    setSelectedRouting("");
    
    // Pour transferUser et transferPhone, le type dans next doit être "transfer"
    const nextType = (type === "transferUser" || type === "transferPhone") ? "transfer" : type;
    onChange(routingName, "next.type", nextType);
    
    // Si c'est un transfer, ajouter targetType
    if (type === "transferUser") {
      onChange(routingName, "next.targetType", "user");
    } else if (type === "transferPhone") {
      onChange(routingName, "next.targetType", "phone");
    }
  };

  const handleTargetSelect = (target) => {
    setSelectedTarget(target);
    if (nextActionType === "queue") {
      onChange(routingName, "next.queueId", target.id);
      onChange(routingName, "next.queueDisplayName", target.displayName);
    } else if (nextActionType === "transferUser") {
      onChange(routingName, "next.targetId", target.id);
      onChange(routingName, "next.userDisplayName", target.displayName);
      onChange(routingName, "next.targetType", "user");
    }
  };

  const handlePhoneChange = (e, val) => {
    setPhoneNumber(val.value);
    onChange(routingName, "next.targetId", val.value);
    onChange(routingName, "next.targetType", "phone");
  };

  const handleRoutingSelect = (event, dataDropdown) => {
    const selectedRoutingName = dataDropdown.optionValue;
    setSelectedRouting(selectedRoutingName);
    onChange(routingName, "next.routingName", selectedRoutingName);
  };

  const routingOptions = Object.keys(allRoutings || {}).filter(
    (key) => key !== routingName
  );

  return (
    <div className={classes.container}>
      {/* Prompt Name */}
      <Label>{t("promptName")}</Label>
      <Input
        className="prompt-input"
        placeholder={t("enterPromptName")}
        value={data?.promptDisplayName || ""}
        disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
        onChange={(e, val) => onChange(routingName, "promptDisplayName", val.value)}
      />

      {/* Generate Audio for this prompt */}
      {data?.audio && (
        <GenerateAudioContainer
          audioFileUrl={data.audio.audioFileUrl}
          text={data.audio.text}
          type={data.audio.type?.toLowerCase() || "text2speech"}
          defaultLanguage={data.audio.language}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          canPlayAudio={permissions?.routing?.canRead}
          setText={(text) => onChange(routingName, "audio.text", text)}
          saveAudio={(url) => onChange(routingName, "audio.audioFileUrl", url)}
          setType={(type) => onChange(routingName, "audio.type", type)}
          setLangue={(lang) => onChange(routingName, "audio.language", lang)}
          setAudio={(selectedMedia) =>
            onChange(routingName, "audio", {
              ...data.audio,
              selectedMedia,
              audioFileUrl: selectedMedia?.url || data.audio.audioFileUrl || "",
              type: "audiofile",
              text: data.audio.text || "",
            })
          }
          fileName={data.audio?.selectedMedia?.header}
             path={`${ACTIVE_TENANT}/${callFlowData.id}/${routingName}`}
        />
      )}

      {/* Next Step */}
      <Label>{t("nextStep")}</Label>
      <Dropdown
        className="prompt-dropdown"
        placeholder={t("selectNextStep")}
        value={getTranslatedValue()}
        selectedOptions={[nextActionType]}
        disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
        onOptionSelect={handleNextActionSelect}
      >
        <Option value="queue">{t("queue")}</Option>
        <Option value="transferUser">{t("transferToUser")}</Option>
        <Option value="disconnect">{t("disconnect")}</Option>
        <Option value="routing">{t("routing")}</Option>
        <Option value="transferPhone">{t("transferToPhone")}</Option>
      </Dropdown>

      {/* Conditional Components */}
      {nextActionType === "queue" && (
        <QueueSelectorComponent
          displayName={
            selectedTarget?.displayName ||
            data?.next?.queueDisplayName ||
            ""
          }
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          getData={(queue) => handleTargetSelect(queue)}
        />
      )}

      {nextActionType === "transferUser" && (
        <SearchResourceInGraph
          displayName={
            selectedTarget?.displayName ||
            data?.next?.userDisplayName ||
            ""
          }
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onSelectUser={(user) => handleTargetSelect(user)}
        />
      )}

      {nextActionType === "routing" && (
        <>
          <Label>{t("selectNextRouting")}</Label>
          <Dropdown
            className="prompt-dropdown"
            placeholder={t("chooseRouting")}
            value={selectedRouting}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onOptionSelect={handleRoutingSelect}
          >
            {routingOptions.length > 0 ? (
              routingOptions.map((routeKey) => (
                <Option key={routeKey} value={routeKey}>
                  {allRoutings[routeKey]?.displayName || routeKey}
                </Option>
              ))
            ) : (
              <Option disabled>{t("noOtherRoutings")}</Option>
            )}
          </Dropdown>
        </>
      )}

      {nextActionType === "transferPhone" && (
        <>
          <Label>{t("phoneNumber")}</Label>
          <Input
            className="prompt-input"
            placeholder="+33 6 12 34 56 78"
            value={phoneNumber}
            disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
            onChange={handlePhoneChange}
          />
        </>
      )}
    </div>
  );
}
